/**
 * The row the listing will occupy, assembled as the form is filled in.
 *
 * Nothing here is decoration: it is the same row geometry the boards use, fed
 * from the form state, so a company can see the thing it is buying rather than
 * a description of it. Parts appear as they become known and hold still once
 * they are there; the only figure that is invented is the rank, and that is
 * read from the live board for the specialism being posted into.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  EMPLOYMENT_TYPE_LABELS,
  MINIMUM_FIRST_BID_CENTS,
  claimRankForAmount,
  type Company,
  type LeaderboardScope,
} from '@bidtohire/shared';

import { useLeaderboard } from '../lib/queries';
import {
  completionRatio,
  listingRequirements,
  previewRow,
  type Requirement,
} from '../lib/listing-preview';
import type { JobFormState } from '../lib/job-form';
import { Money } from './Money';
import { CompanyMark } from './ui';

/* -------------------------------------------------------------------------- */
/* Pieces                                                                     */
/* -------------------------------------------------------------------------- */

function CheckDisc({ done, size = 16 }: { readonly done: boolean; readonly size?: number }): ReactNode {
  return (
    <span
      className={done ? 'disc is-done' : 'disc'}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {done ? (
        <svg width={size - 6} height={size - 6} viewBox="0 0 12 12" fill="none">
          <path
            className="tick"
            d="M2.6 6.2l2.3 2.3L9.4 3.9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </span>
  );
}

/**
 * Where this listing lands on the chosen specialism, and how big that board
 * becomes once it joins. Rendered only once a scope is known, so the query is
 * never fired against a board nobody has chosen.
 *
 * Two cases, and they are not the same arithmetic.
 *
 * An unpaid listing is previewing an opening bid at the floor. Every published
 * listing has paid at least that floor and a tie keeps the earlier bid ahead,
 * so an opening bid ties-or-loses against every row and lands last by
 * construction — rank is the board's whole size plus one. Counting the loaded
 * page instead would cap the rank at the page size while the denominator kept
 * the true total, which is how "#26 of 76" and "#2 of 1" get printed.
 *
 * A reservation that is already paid for holds a real total, which may sit
 * anywhere on the board. The loaded page is the top slice, so as long as one
 * loaded row is below that total the count above it is exact. When the total
 * is at or below every row on the page and the page is not the whole board,
 * the position is genuinely unknown here — and this returns nothing rather
 * than print a number it cannot stand behind.
 */
function useProjectedRank(
  scope: LeaderboardScope | undefined,
  paidCents: number | undefined,
): {
  readonly rank: number | undefined;
  readonly total: number | undefined;
} {
  const board = useLeaderboard(scope ?? { kind: 'global' }, { enabled: scope !== undefined });
  if (scope === undefined || board.data === undefined) return { rank: undefined, total: undefined };
  const first = board.data.pages[0];
  if (first === undefined) return { rank: undefined, total: undefined };
  const joined = first.totalListings + 1;

  if (paidCents === undefined) return { rank: joined, total: joined };

  const totals = first.items.map((entry) => entry.totalPaidCents);
  const rank = claimRankForAmount(paidCents, totals);
  const belowEveryLoadedRow = rank > totals.length;
  const pageIsWholeBoard = totals.length >= first.totalListings;
  if (belowEveryLoadedRow && !pageIsWholeBoard) return { rank: undefined, total: joined };
  return { rank: belowEveryLoadedRow ? joined : rank, total: joined };
}

/* -------------------------------------------------------------------------- */
/* The row                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The row itself, from plain values. The posting form feeds it what has been
 * typed; the beat after a payment feeds it what was bought.
 */
export function BoardRowPreview({
  rank,
  company,
  title,
  subline,
  salary,
  location,
  priceCents,
  popSalary = false,
}: {
  readonly rank: ReactNode;
  readonly company: Pick<Company, 'name' | 'slug'> & { readonly logoKey?: string };
  readonly title: string | undefined;
  readonly subline: string;
  readonly salary?: string | undefined;
  readonly location?: string | undefined;
  readonly priceCents?: number | undefined;
  readonly popSalary?: boolean;
}): ReactNode {
  const facts = salary !== undefined || location !== undefined || priceCents !== undefined;

  return (
    <div className="pv-row">
      <span className="pv-rank mono">{rank}</span>
      <CompanyMark company={company} className="pv-mark" />
      <span className="pv-text">
        {title === undefined ? (
          <span className="pv-ghost" aria-hidden="true" />
        ) : (
          <span className="pv-title">{title}</span>
        )}
        <span className="pv-sub">{subline}</span>
      </span>
      <span className={facts ? 'pv-collapse is-open' : 'pv-collapse'}>
        <span className="pv-facts">
          {salary === undefined ? null : (
            <span className={popSalary ? 'pv-chip mono is-new' : 'pv-chip mono'}>{salary}</span>
          )}
          {salary !== undefined && location !== undefined ? (
            <span className="pv-sep" aria-hidden="true">
              |
            </span>
          ) : null}
          {location === undefined ? null : <span className="pv-where">{location}</span>}
          {priceCents === undefined ? null : (
            <>
              {salary === undefined && location === undefined ? null : (
                <span className="pv-sep" aria-hidden="true">
                  |
                </span>
              )}
              <Money cents={priceCents} className="pv-price" />
            </>
          )}
        </span>
      </span>
    </div>
  );
}

function PreviewRowBody({
  state,
  company,
  rank,
  priceCents,
  popSalary,
}: {
  readonly state: JobFormState;
  readonly company: Pick<Company, 'name' | 'slug'> & { readonly logoKey?: string };
  readonly rank: number | undefined;
  readonly priceCents: number;
  readonly popSalary: boolean;
}): ReactNode {
  const row = previewRow(state);
  const employment = state.employmentType
    .map((value) => EMPLOYMENT_TYPE_LABELS[value])
    .slice(0, 1)
    .join('');
  const subline = [company.name, row.scopePath, employment === '' ? undefined : employment]
    .filter((part) => part !== undefined && part !== '')
    .join(' · ');

  return (
    <BoardRowPreview
      rank={rank === undefined ? '—' : rank}
      company={company}
      title={row.title}
      subline={subline}
      salary={row.salary}
      location={row.location}
      priceCents={priceCents}
      popSalary={popSalary}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* The rail                                                                   */
/* -------------------------------------------------------------------------- */

export function ListingPreview({
  state,
  company,
  submitLabel,
  busy,
  paidCents,
}: {
  readonly state: JobFormState;
  readonly company: Pick<Company, 'name' | 'slug'> & { readonly logoKey?: string };
  readonly submitLabel: string;
  readonly busy: boolean;
  /**
   * What this listing has already paid, when it is a reservation waiting for
   * its details. Its presence changes what the rail is describing: not what an
   * opening bid would buy, but what this listing has bought already.
   */
  readonly paidCents?: number;
}): ReactNode {
  const requirements = listingRequirements(state);
  const ratio = completionRatio(requirements);
  const row = previewRow(state);

  const scope: LeaderboardScope | undefined =
    state.category === '' || state.subcategory === ''
      ? undefined
      : { kind: 'subcategory', category: state.category, subcategory: state.subcategory };
  const { rank, total } = useProjectedRank(scope, paidCents);
  const rowPriceCents = paidCents ?? MINIMUM_FIRST_BID_CENTS;

  // The chip pops the first time the range becomes a pair, and never again:
  // a company editing the top of its range does not need it to bounce.
  const popped = useRef(false);
  const hasSalary = row.salary !== undefined;
  const popSalary = hasSalary && !popped.current;
  useEffect(() => {
    if (hasSalary) popped.current = true;
  }, [hasSalary]);

  const [sheetOpen, setSheetOpen] = useState(false);

  const rankLine =
    rank === undefined || total === undefined || row.specialismName === undefined ? null : (
      <>
        {' '}
        On {row.specialismName} today that is <span className="pv-hash mono">#{rank}</span> of{' '}
        {total}
        {total > 1 && paidCents === undefined ? (
          <>
            {' '}
            — last, because every listing there has already paid at least{' '}
            <Money cents={MINIMUM_FIRST_BID_CENTS} /> and a tie keeps the earlier bid ahead. Bid
            higher to open further up
          </>
        ) : null}
        .
      </>
    );

  return (
    <aside className="postrail" aria-label="Your listing so far">
      <div className="card railcard">
        <span className="railmeter" aria-hidden="true">
          <span className="railmeter-fill" style={{ width: `${Math.round(ratio * 100)}%` }} />
        </span>

        <div className="railbody">
          <div className="railhead">
            <span className="cap">Your row</span>
            <span className="mono railscope">{row.specialismName ?? 'No specialism yet'}</span>
          </div>

          <div className="pv-frame">
            <PreviewRowBody
              state={state}
              company={company}
              rank={rank}
              priceCents={rowPriceCents}
              popSalary={popSalary}
            />
          </div>

          <p className="railnote">
            {paidCents === undefined ? (
              <>
                <Money cents={MINIMUM_FIRST_BID_CENTS} className="pv-price" /> publishes this.
              </>
            ) : (
              <>
                <Money cents={paidCents} className="pv-price" /> is paid. Submitting publishes
                this.
              </>
            )}
            {rankLine}
          </p>
        </div>

        <div className="railfoot">
          <span className="cap">Required</span>
          <ul className="railreqs">
            {requirements.map((requirement: Requirement) => (
              <li key={requirement.key} className={requirement.done ? 'is-done' : undefined}>
                <CheckDisc done={requirement.done} />
                {requirement.label}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="small muted railcaption">
        The row updates as you type and follows you down the page.
      </p>

      {/* The same thing on a phone: pinned to the bottom, opening to the full
          row when it is asked for. */}
      <div className="postbar">
        <span className="railmeter" aria-hidden="true">
          <span className="railmeter-fill" style={{ width: `${Math.round(ratio * 100)}%` }} />
        </span>

        {sheetOpen ? (
          <div className="postbar-sheet">
            <div className="pv-frame">
              <PreviewRowBody
                state={state}
                company={company}
                rank={rank}
                priceCents={rowPriceCents}
                popSalary={false}
              />
            </div>
            <ul className="railreqs">
              {requirements.map((requirement) => (
                <li key={requirement.key} className={requirement.done ? 'is-done' : undefined}>
                  <CheckDisc done={requirement.done} />
                  {requirement.label}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="postbar-row">
          <CompanyMark company={company} className="postbar-mark" />
          <span className="postbar-text">
            <span className="pv-title">{row.title ?? 'Your listing'}</span>
            <span className="postbar-facts">
              <span className="mono">{row.salary ?? 'No range yet'}</span>
              <span className="pv-sep" aria-hidden="true">
                |
              </span>
              <span className="mono pv-hash">
                {rank === undefined ? '—' : `#${rank}`} for <Money cents={rowPriceCents} />
              </span>
            </span>
          </span>
          <button
            type="button"
            className="postbar-toggle"
            aria-expanded={sheetOpen}
            aria-label={sheetOpen ? 'Hide the full row' : 'Show the full row'}
            onClick={() => setSheetOpen((was) => !was)}
          >
            <svg
              width="14"
              height="9"
              viewBox="0 0 14 9"
              fill="none"
              aria-hidden="true"
              className={sheetOpen ? 'is-flipped' : undefined}
            >
              <path
                d="M1 8l6-6 6 6"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="postbar-action">
          <button type="submit" className="pill pill-primary pill-block" disabled={busy}>
            {busy ? 'Saving…' : submitLabel}
          </button>
        </div>
      </div>
    </aside>
  );
}
